export interface Rgb {
  r: number;
  g: number;
  b: number;
}

/** Parse "#rgb" / "#rrggbb" (with or without #) → Rgb, or null if invalid. */
export function hexToRgb(hex: string): Rgb | null {
  const h = hex.trim().replace(/^#/, "");
  const full =
    h.length === 3
      ? h
          .split("")
          .map((c) => c + c)
          .join("")
      : h;
  if (!/^[0-9a-fA-F]{6}$/.test(full)) return null;
  return {
    r: Number.parseInt(full.slice(0, 2), 16),
    g: Number.parseInt(full.slice(2, 4), 16),
    b: Number.parseInt(full.slice(4, 6), 16),
  };
}

const clamp = (n: number, lo: number, hi: number) =>
  Math.min(hi, Math.max(lo, n));

/** Rgb (0-255) → "#rrggbb". */
export function rgbToHex({ r, g, b }: Rgb): string {
  const to = (n: number) =>
    clamp(Math.round(n), 0, 255).toString(16).padStart(2, "0");
  return `#${to(r)}${to(g)}${to(b)}`;
}

/** WCAG relative luminance of an sRGB colour (0-1). */
export function relativeLuminance({ r, g, b }: Rgb): number {
  const lin = (c: number) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
}

/** WCAG contrast ratio between two colours (1-21). */
export function contrastRatio(a: Rgb, b: Rgb): number {
  const l1 = relativeLuminance(a);
  const l2 = relativeLuminance(b);
  const [hi, lo] = l1 >= l2 ? [l1, l2] : [l2, l1];
  return (hi + 0.05) / (lo + 0.05);
}

export interface WcagResult {
  ratio: number;
  aaNormal: boolean;
  aaLarge: boolean;
  aaaNormal: boolean;
  aaaLarge: boolean;
}

/** WCAG 2.1 pass/fail for a contrast ratio (large text = ≥18.66px bold / 24px). */
export function wcag(ratio: number): WcagResult {
  return {
    ratio,
    aaNormal: ratio >= 4.5,
    aaLarge: ratio >= 3,
    aaaNormal: ratio >= 7,
    aaaLarge: ratio >= 4.5,
  };
}

export interface Hsl {
  h: number;
  s: number;
  l: number;
}

export function rgbToHsl({ r, g, b }: Rgb): Hsl {
  const rr = r / 255;
  const gg = g / 255;
  const bb = b / 255;
  const max = Math.max(rr, gg, bb);
  const min = Math.min(rr, gg, bb);
  const d = max - min;
  const l = (max + min) / 2;
  let h = 0;
  let s = 0;
  if (d !== 0) {
    s = d / (1 - Math.abs(2 * l - 1));
    switch (max) {
      case rr:
        h = ((gg - bb) / d) % 6;
        break;
      case gg:
        h = (bb - rr) / d + 2;
        break;
      default:
        h = (rr - gg) / d + 4;
    }
    h *= 60;
    if (h < 0) h += 360;
  }
  return { h, s: s * 100, l: l * 100 };
}

export function hslToRgb({ h, s, l }: Hsl): Rgb {
  const ss = s / 100;
  const ll = l / 100;
  const c = (1 - Math.abs(2 * ll - 1)) * ss;
  const hh = (((h % 360) + 360) % 360) / 60;
  const x = c * (1 - Math.abs((hh % 2) - 1));
  const m = ll - c / 2;
  let r = 0;
  let g = 0;
  let b = 0;
  if (hh < 1) [r, g, b] = [c, x, 0];
  else if (hh < 2) [r, g, b] = [x, c, 0];
  else if (hh < 3) [r, g, b] = [0, c, x];
  else if (hh < 4) [r, g, b] = [0, x, c];
  else if (hh < 5) [r, g, b] = [x, 0, c];
  else [r, g, b] = [c, 0, x];
  return {
    r: (r + m) * 255,
    g: (g + m) * 255,
    b: (b + m) * 255,
  };
}

export type Harmony =
  | "complementary"
  | "analogous"
  | "triadic"
  | "tetradic"
  | "monochromatic";

/** Generate a harmonious palette (hex strings) from a base colour. */
export function palette(baseHex: string, harmony: Harmony): string[] {
  const rgb = hexToRgb(baseHex);
  if (!rgb) return [];
  const base = rgbToHsl(rgb);
  const at = (h: number, l = base.l) =>
    rgbToHex(hslToRgb({ h: (h + 360) % 360, s: base.s, l }));
  switch (harmony) {
    case "complementary":
      return [at(base.h), at(base.h + 180)];
    case "analogous":
      return [at(base.h - 30), at(base.h), at(base.h + 30)];
    case "triadic":
      return [at(base.h), at(base.h + 120), at(base.h + 240)];
    case "tetradic":
      return [at(base.h), at(base.h + 90), at(base.h + 180), at(base.h + 270)];
    case "monochromatic":
      return [
        at(base.h, clamp(base.l - 30, 0, 100)),
        at(base.h, clamp(base.l - 15, 0, 100)),
        at(base.h),
        at(base.h, clamp(base.l + 15, 0, 100)),
        at(base.h, clamp(base.l + 30, 0, 100)),
      ];
  }
}

/** Pixels-per-inch from a resolution and diagonal screen size (inches). */
export function ppi(
  widthPx: number,
  heightPx: number,
  diagonalIn: number,
): number | null {
  if (diagonalIn <= 0 || widthPx <= 0 || heightPx <= 0) return null;
  return Math.sqrt(widthPx ** 2 + heightPx ** 2) / diagonalIn;
}
